import { useSyncExternalStore } from 'react';

/**
 * App-wide "the voter's ballot is still loading" flag, so chat can hold a send
 * until the ballot — and the chat context built from it — is ready. A question
 * sent mid-load reached the agent with no ballot, which then told a voter
 * looking at their address that none was on file.
 *
 * Each ballot view reports under its own owner key, so one view finishing
 * never clears another's load.
 */
const loadingOwners = new Set<symbol>();
const listeners = new Set<() => void>();

export const setBallotLoading = (owner: symbol, loading: boolean): void => {
  const had = loadingOwners.has(owner);
  if (loading === had) return;
  if (loading) loadingOwners.add(owner);
  else loadingOwners.delete(owner);
  listeners.forEach((listener) => listener());
};

export const isBallotLoading = (): boolean => loadingOwners.size > 0;

/**
 * Hold chat sends until `work` settles, capped at `maxMs` so a request that
 * never settles cannot leave chat locked. Used around chat-context fetches:
 * a ballot can be on screen while the context built from it is still loading.
 */
export const holdChatUntil = async <T>(work: Promise<T>, maxMs = 30_000): Promise<T> => {
  const owner = Symbol('hold-chat');
  setBallotLoading(owner, true);
  const release = setTimeout(() => setBallotLoading(owner, false), maxMs);
  try {
    return await work;
  } finally {
    clearTimeout(release);
    setBallotLoading(owner, false);
  }
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const useBallotLoading = (): boolean =>
  useSyncExternalStore(subscribe, isBallotLoading, () => false);

export default useBallotLoading;
